/**
 * AICopyReviewPanel — Phase 3 of the 7-day onboarding pipeline.
 *
 * Reads /api/directory/admin/ai-copy-review/{slug} for cached suggestions
 * and lets owner trigger a fresh Gemini 3 Flash review or one-tap "Apply"
 * any individual suggestion to the entity.
 *
 * Demonstrates the per-page AI value that justifies premium onboarding fees.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Sparkles,
  Loader2,
  CheckCircle2,
  ArrowRight,
  Search,
  Zap,
  ShieldCheck,
  Brain,
  TrendingUp,
  Globe,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const CATEGORY_META = {
  seo:        { icon: Search, color: "blue",    label: "SEO" },
  aeo:        { icon: Brain,  color: "fuchsia", label: "AEO (AI Search)" },
  conversion: { icon: Zap,    color: "amber",   label: "Conversion" },
  trust:      { icon: ShieldCheck, color: "emerald", label: "Trust" },
};

const IMPACT_BADGE = {
  high:   "bg-red-100 text-red-800",
  medium: "bg-amber-100 text-amber-800",
  low:    "bg-stone-100 text-stone-700",
};

export default function AICopyReviewPanel() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [data, setData] = useState(null);
  const [applied, setApplied] = useState({});
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/directory/admin/ai-copy-review/${slug}`);
      const d = await r.json();
      setData(d);
      setApplied(d.applied || {});
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/directory/admin/ai-copy-review/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entity_slug: slug }),
      });
      const d = await r.json();
      if (!d.ok) setError(d.error || "review failed");
      else {
        setData({ ...data, ...d });
        setApplied({});
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setRunning(false);
    }
  };

  const apply = async (s) => {
    try {
      const r = await fetch(`${API}/api/directory/admin/ai-copy-review/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entity_slug: slug,
          suggestion_id: s.id,
          value: s.suggested,
        }),
      });
      const d = await r.json();
      if (d.ok) setApplied((a) => ({ ...a, [s.id]: true }));
    } catch (e) {
      setError(String(e));
    }
  };

  const suggestions = data?.suggestions || [];
  const runAt = data?.run_at ? new Date(data.run_at).toLocaleString() : null;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="ai-review-page">
      <header className="bg-gradient-to-r from-fuchsia-900 to-stone-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-8 sm:py-10">
          <Link to={`/r/${slug}`} className="text-[12px] text-stone-300 hover:text-white">
            ← Back to mini-site
          </Link>
          <div className="flex items-center gap-2 mt-4 mb-2">
            <Sparkles className="w-5 h-5 text-fuchsia-300" />
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-fuchsia-300">
              Phase 3 · AI Copy Review · powered by Gemini 3 Flash
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">SEO + AEO recommendations</h1>
          <p className="text-stone-300 mt-3 max-w-2xl text-[14px] sm:text-[15px]">
            Specific improvements to make {slug.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())} rank higher on Google and
            be picked up by AI search engines (ChatGPT, Perplexity, Gemini Search). One-tap to apply.
          </p>
          {runAt && (
            <div className="mt-3 text-[12px] text-stone-400">
              Last reviewed: {runAt} · against{" "}
              <a href={data?.url} target="_blank" rel="noopener noreferrer" className="underline">
                {data?.url}
              </a>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-[13px] text-stone-600">
            {suggestions.length > 0
              ? `${suggestions.length} suggestion${suggestions.length === 1 ? "" : "s"} ready`
              : "No suggestions yet — run a review to get started."}
          </div>
          <button
            onClick={run}
            disabled={running}
            data-testid="ai-review-run-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-fuchsia-700 text-white text-[14px] font-bold hover:bg-fuchsia-800 disabled:opacity-50"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {running ? "AI reading your site…" : runAt ? "Re-run review" : "Run AI review"}
          </button>
        </div>

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-800 text-[13px]" data-testid="ai-review-error">
            {error}
          </div>
        )}

        {loading && !data && (
          <div className="text-center py-12 text-stone-500">
            <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" /> Loading…
          </div>
        )}

        <div className="space-y-3" data-testid="ai-review-suggestions">
          {suggestions.map((s, i) => {
            const meta = CATEGORY_META[s.category] || CATEGORY_META.seo;
            const Icon = meta.icon;
            const isApplied = !!applied[s.id];
            return (
              <div
                key={s.id || i}
                className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`ai-suggestion-${s.id}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className={`shrink-0 w-10 h-10 rounded-xl bg-${meta.color}-100 flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 text-${meta.color}-700`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-${meta.color}-50 text-${meta.color}-800`}>
                        {meta.label}
                      </span>
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${IMPACT_BADGE[s.impact] || IMPACT_BADGE.medium}`}>
                        {s.impact} impact
                      </span>
                      <span className="text-[11px] text-stone-400 font-mono">{s.field}</span>
                    </div>
                  </div>
                  {isApplied ? (
                    <span className="inline-flex items-center gap-1 text-[12px] font-bold text-emerald-700">
                      <CheckCircle2 className="w-4 h-4" /> Applied
                    </span>
                  ) : (
                    <button
                      onClick={() => apply(s)}
                      data-testid={`ai-suggestion-apply-${s.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-stone-900 text-white text-[12px] font-bold hover:bg-stone-700"
                    >
                      Apply <ArrowRight className="w-3 h-3" />
                    </button>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <div className="rounded-lg bg-stone-50 border border-stone-200 p-3">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-1">Current</div>
                    <div className="text-[13px] text-stone-700 font-mono break-words">{s.current || <span className="italic">missing</span>}</div>
                  </div>
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-1">Suggested</div>
                    <div className="text-[13px] text-stone-900 font-mono break-words">{s.suggested}</div>
                  </div>
                </div>

                <div className="flex items-start gap-2 text-[13px] text-stone-600">
                  <TrendingUp className="w-3.5 h-3.5 mt-0.5 shrink-0 text-stone-400" />
                  <span>{s.reasoning}</span>
                </div>
              </div>
            );
          })}
        </div>

        {!suggestions.length && !loading && (
          <div className="bg-white rounded-2xl border-2 border-dashed border-stone-300 p-10 text-center">
            <Globe className="w-10 h-10 mx-auto text-stone-300 mb-3" />
            <h3 className="font-bold text-stone-800 mb-1">No review yet</h3>
            <p className="text-[13px] text-stone-500 max-w-md mx-auto">
              Click <span className="font-bold">Run AI review</span> above. The AI will read this venue's
              live homepage and emit 3-5 targeted SEO + AEO improvements — usually takes 5-10 seconds.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
